/**
 * Records that are saved on this device but too large to sync.
 *
 * The Rust record writes (`extension_records_upsert`, `extension_records_sync_scope`)
 * save the row locally and then queue a sync op — unless the op is over the
 * per-op cap (`OUTBOX_ITEM_MAX_BYTES`, 250 000 bytes since 14.10.2026; 32 000 before). Before 26.09.2026 that
 * refusal was a log line: the editor said "Saved", and the document never
 * reached the user's other devices or the team again. The writes now answer
 * with a `SyncOutcome` (`src-tauri/src/sync/record_outcome.rs`):
 *
 *   { status: 'queued' | 'unchanged' | 'local_only' }
 *   { status: 'too_large', bytes, cap }
 *
 * `recordsClient` hands every answer to `noteRecordWrite` / `noteScopeWrite`;
 * the banner (`components/SyncTooLargeBanner.js`) reads this store. An entry
 * lives until a later write of the same record queues again (the user
 * shortened it) or the record leaves its scope.
 */
import { create } from 'zustand';

const CLEARS = new Set(['queued', 'local_only']);

export const useSyncSizeStore = create(() => ({
  /** recordId -> { bytes, cap, scopeId } */
  tooLarge: {},
}));

function apply(recordId, scopeId, outcome, next) {
  if (!recordId || !outcome || typeof outcome !== 'object') return;
  if (outcome.status === 'too_large') {
    next[recordId] = { bytes: Number(outcome.bytes) || 0, cap: Number(outcome.cap) || 0, scopeId };
  } else if (CLEARS.has(outcome.status)) {
    delete next[recordId];
  }
}

/** After `upsertRecord(args)` resolved with `outcome`. */
export function noteRecordWrite(args, outcome) {
  const record = args?.record;
  if (!record?.id) return;
  const scopeId = String(record.data?.scope_id ?? record.id);
  const next = { ...useSyncSizeStore.getState().tooLarge };
  apply(String(record.id), scopeId, outcome, next);
  useSyncSizeStore.setState({ tooLarge: next });
}

/** After `syncRecordScope(args)` resolved with `report` ({ changed, results }). */
export function noteScopeWrite(args, report) {
  if (!args?.scopeId) return;
  const scopeId = String(args.scopeId);
  const next = { ...useSyncSizeStore.getState().tooLarge };
  // A record that is no longer part of the scope was tombstoned by this write.
  const present = new Set((args.records || []).map((r) => String(r.id)));
  for (const [id, entry] of Object.entries(next)) {
    if (entry.scopeId === scopeId && !present.has(id)) delete next[id];
  }
  for (const r of Array.isArray(report?.results) ? report.results : []) {
    apply(String(r.id), scopeId, r, next);
  }
  useSyncSizeStore.setState({ tooLarge: next });
}

/** The too-large records of one scope (a document id, a board id), largest first. */
export function tooLargeInScope(tooLarge, scopeId) {
  const key = String(scopeId);
  return Object.entries(tooLarge)
    .filter(([, e]) => e.scopeId === key)
    .map(([id, e]) => ({ id, ...e }))
    .sort((a, b) => b.bytes - a.bytes);
}
